/**
 * Deterministic transcript-entry stamping — ids, turn numbers, and sequence
 * order with no wall clock and no randomness (architecture.md §7.3, D08/D09).
 *
 * @remarks
 * Every entry emitted while executing one turn shares that turn's number and
 * takes the next position in a per-turn sequence, so an entry's id
 * (`turn-3-2`, or `session-start-0` for the turn-0 opening emission) is a
 * pure function of WHEN in the session it fired. Replays reproduce ids
 * byte-for-byte; React keys and replay-fixture expectations get stable
 * handles for free.
 *
 * The cursor is a VALUE, not a counter object: stamping returns the advanced
 * cursor alongside the entries, keeping the whole pipeline
 * readonly-in/fresh-out (architecture.md §3.2).
 *
 * @packageDocumentation
 */

import type { NarrativeLine, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { resolveConditionalText } from '../narrative/conditional-text.js';

/** Where the next transcript entry lands: which turn, and which position within it. */
export interface TranscriptCursor {
	/** Id prefix — `turn-<n>` for executed turns, `session-start` for the turn-0 opening emission. */
	readonly idPrefix: string;
	/** The next entry's position within this turn (0-based). */
	readonly nextSequence: number;
	/** The turn number stamped onto entries (`RuntimeTranscriptEntry.turnCount`). */
	readonly turnNumber: number;
}

/** Entries stamped so far plus the cursor to continue from. */
export interface StampedNarratives {
	readonly cursor: TranscriptCursor;
	readonly entries: readonly RuntimeTranscriptEntry[];
}

/**
 * Open the cursor for the session's turn-0 opening emission
 * (`createInitialRuntimeState`'s entry-room beats).
 *
 * @returns A cursor stamping `session-start-*` ids at turn 0
 */
export const createSessionStartCursor = (): TranscriptCursor => ({
	idPrefix: 'session-start',
	nextSequence: 0,
	turnNumber: 0,
});

/**
 * Open the cursor for one executed turn.
 *
 * @param turnNumber - The turn the action BECOMES (current `turnCount` + 1 for a time-consuming action)
 * @returns A cursor stamping `turn-<turnNumber>-*` ids
 */
export const createTurnCursor = (turnNumber: number): TranscriptCursor => ({
	idPrefix: `turn-${String(turnNumber)}`,
	nextSequence: 0,
	turnNumber,
});

/**
 * Stamp authored or default narrative lines into transcript entries at the
 * cursor, in order.
 *
 * @param cursor - Where these entries land
 * @param lines - The lines to stamp, in emission order
 * @param triggerContext - Optional provenance tag (e.g. `first_visit:room_vault`) carried onto every stamped entry
 * @returns The stamped entries and the advanced cursor
 */
export const stampNarrativeLines = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	lines: readonly NarrativeLine[],
	triggerContext?: string,
): StampedNarratives => {
	const entries = lines.map(
		(line, lineIndex): RuntimeTranscriptEntry => ({
			// D264: a line's sounds ride its entry, unresolved — they caption nothing until a host plays them.
			...(line.cues === undefined ? {} : { cues: line.cues }),
			id: `${cursor.idPrefix}-${String(cursor.nextSequence + lineIndex)}`,
			priority: line.priority,
			source: line.source,
			// D74: authored text is conditional text EVERYWHERE, and this is the
			// one place it resolves — entries carry the chosen reading, so the
			// transcript, saves, and replays stay plain resolved strings.
			text: resolveConditionalText(line.text, bundle, state),
			...(triggerContext === undefined ? {} : { triggerContext }),
			turnCount: cursor.turnNumber,
		}),
	);

	return {
		cursor: { ...cursor, nextSequence: cursor.nextSequence + lines.length },
		entries,
	};
};

/** The state with entries appended to its transcript — the one way anything commits lines. */
export const appendTranscript = (state: RuntimeState, entries: readonly RuntimeTranscriptEntry[]): RuntimeState => ({
	...state,
	session: { ...state.session, transcript: [...state.session.transcript, ...entries]},
});

/** A cursor in this input's id namespace, stamping the CURRENT turn count (inputs consume no time themselves). */
export const inputNamespaceCursor = (inputOrdinal: number, sequence: number, state: RuntimeState): TranscriptCursor => ({
	idPrefix: `input-${String(inputOrdinal)}`,
	nextSequence: sequence,
	turnNumber: state.progression.turnCount,
});
